import React, { useEffect, useState } from "react";
import {
	FormValidator,
	useFormValidator,
	Inputs,
	TextArea,
	useTariffableInputs,
} from "coaz-ui";
import client from "../client";

const EditCpd = ({
	params,
	submitRef,
	setLoading,
	setEnableSubmit,
	initialCourse,
}) => {
	const [course, setCourse] = useState(null);
	const minWidth = 240;
	const { request, errorMessage } = client;

	const submit = async () => {
		const courseId = course?.ref?.id ?? course?.id;
		/* console.info("[EditCourse] submitting update", {
      endpoint: "cpds",
      id: courseId,
      fields: {
        name: course?.name,
        points: course?.points,
        availableFrom: course?.availableFrom,
        availableTo: course?.availableTo,
        available: course?.available,
        visibleWhenUnavailable: course?.visibleWhenUnavailable,
        description: course?.description,
      },
    }); */

		try {
			const response = await request("PUT", "cpds", course);
			/* console.info("[EditCourse] update response", {
        id: response?.ref?.id,
        name: response?.name,
        hasReference: !!response?.ref,
      }); */

			if (response && response.ref) {
				const saved = await request("GET", `cpds/${courseId}`);
				const fieldsToCheck = [
					"name",
					"points",
					"availableFrom",
					"availableTo",
					"available",
					"visibleWhenUnavailable",
					"description",
				];
				const normalize = (field, value) => {
					if (value == null) return null;
					if (field === "availableFrom" || field === "availableTo") {
						const date =
							value instanceof Date ? value : new Date(value);
						return isNaN(date)
							? String(value)
							: date.toISOString().slice(0, 10);
					}
					return String(value);
				};
				const mismatches = fieldsToCheck.filter(
					(field) =>
						normalize(field, saved?.[field]) !==
						normalize(field, course?.[field]),
				);

				/* console.info("[EditCourse] persisted update check", {
          id: courseId,
          savedFields: Object.fromEntries(fieldsToCheck.map((field) => [field, saved?.[field]])),
          mismatches,
        }); */

				if (mismatches.length > 0) {
					return {
						message: `The server accepted the update but did not save these fields: ${mismatches.join(", ")}`,
					};
				}
				return { message: "Successful", success: true };
			}
			return { message: errorMessage(response) };
		} catch (error) {
			console.error("[EditCourse] update failed", {
				status: error?.response?.status,
				message: error?.message,
			});
			return { message: errorMessage(error) };
		}
	};

	const [register, handleChange, handleSubmit, errors] =
		useFormValidator(submit);

	const { inputs: tariffInputs } = useTariffableInputs(
		course,
		setCourse,
		request,
		handleChange,
		register,
		errors,
	);
	const safeTariffInputs = tariffInputs.map((input) => ({
		...input,
		register: undefined,
		checks: undefined,
		errors: undefined,
	}));

	const onChange = (e) => {
		const value = e.target.value;
		setCourse((prev) => ({
			...prev,
			[e.target.name]: value === "" ? null : value,
		}));
	};

	const onNumber = (e) => {
		const value = e.target.value;
		if (isNaN(value)) return;
		setCourse((prev) => ({
			...prev,
			[e.target.name]: value === "" ? null : value,
		}));
	};

	const onDate = (e) => {
		const value = e.target.value;
		setCourse((prev) => ({
			...prev,
			[e.target.name]: value ? new Date(value) : null,
		}));
	};

	const inputs = [
		safeTariffInputs[0],
		{
			label: "Points",
			type: "number",
			name: "points",
			value: course && course.points ? course.points : "",
			placeholder: "Enter points...",
			onChange: (e) => handleChange(e, onNumber),
		},
		{
			label: "Available from",
			type: "date",
			name: "availableFrom",
			value:
				course && course.availableFrom
					? course.availableFrom.toISOString().slice(0, 10)
					: "",
			placeholder: "Available from...",
			onChange: (e) => handleChange(e, onDate),
		},
		{
			label: "Available upto",
			type: "date",
			name: "availableTo",
			value:
				course && course.availableTo
					? course.availableTo.toISOString().slice(0, 10)
					: "",
			placeholder: "Available upto...",
			onChange: (e) => handleChange(e, onDate),
		},
		{
			label: "Visible when unavailable",
			type: "checkbox",
			name: "visibleWhenUnavailable",
			value: course ? !!course.visibleWhenUnavailable : false,
			onChange: () =>
				setCourse((prev) => ({
					...prev,
					visibleWhenUnavailable: !prev.visibleWhenUnavailable,
				})),
		},
		safeTariffInputs[1],
		safeTariffInputs[2],
		{ type: "blank", name: "course-input-spacer" },
	];

	useEffect(() => {
		if (course) {
			submitRef.current = {
				submit: () => {
					if (!course.name?.trim()) {
						return { message: "Name is required" };
					}
					return submit();
				},
			};
			setEnableSubmit(true);
		} else {
			submitRef.current = null;
			setEnableSubmit(false);
		}
		return () => {
			submitRef.current = null;
		};
	}, [course]);

	useEffect(() => {
		(async () => {
			if (!params || !params.id) return;
			setLoading && setLoading(true);

			let loaded = null;
			if (
				initialCourse &&
				String(initialCourse.ref?.id ?? initialCourse.id) ===
					String(params.id)
			) {
				loaded = { ...initialCourse };
			} else {
				try {
					const response = await request("GET", `cpds/${params.id}`);
					if (response && response.ref) loaded = response;
				} catch (error) {
					console.log(error);
				}
			}

			if (loaded) {
				loaded.availableFrom = loaded.availableFrom
					? new Date(loaded.availableFrom)
					: null;
				loaded.availableTo = loaded.availableTo
					? new Date(loaded.availableTo)
					: null;
			}

			setCourse(loaded);
			setLoading && setLoading(false);
		})();
	}, []);

	return (
		<FormValidator>
			{course && (
				<div className="flex flex-col w-full h-auto">
					<div className="flex flex-col w-full h-auto shrink-0 space-y-4">
						<Inputs
							inputs={inputs}
							minWidth={minWidth}
							onSubmit={handleSubmit}
						/>
						<TextArea
							label="Description"
							id="description"
							name="description"
							value={course.description ? course.description : ""}
							placeholder="Enter description"
							onChange={(e) => handleChange(e, onChange)}
						/>
					</div>
				</div>
			)}
		</FormValidator>
	);
};

export default EditCpd;
